import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetLeadCardQuery } from '@app/api/Client/api';
import type { GetLeadCardResponse } from '@app/api/Client/types';
import type { ModelLead } from '@app/api/Leads/types';
import type {
  LeadCardBranchItemData,
  LeadCardCheckboxItemData,
  LeadCardContactMethodData,
  LeadCardIntegrationItemData,
  LeadCardUserDetailsData,
  LeadCardResponsibleEmployeeData,
} from '@app/components/LeadCard/data';
import {
  LEAD_CARD_CUSTOM_ACTIONS,
  normalizeId,
} from '@app/components/LeadCard/model/filterStorage';
import {
  clearLeadCardRefresh,
  selectPendingLeadCardRefreshId,
} from '@app/components/LeadCard/model/leadCardRefreshSlice';
import { useLeadResponsibleChange } from '@app/components/LeadCard/model/useLeadResponsibleChange';
import {
  EMAIL_HANDLER,
  INSTAGRAM,
  INTEGRATIONS_DICTIONARY,
  MAX_MESSENGER,
  TELEGRAM,
  TELEGRAM_PERSONAL,
  VK,
} from '@app/constants';
import { useClientsAccessRights, useUserPrint } from '@app/hooks';
import { useErrorHandler } from '@app/shared/hooks';
import { toTagItems } from '@app/shared/utils';
import { useAppDispatch, useAppSelector } from '@app/store';
import type { NClients, NSmallGlobalEntities } from '@app/types';

import { useLeadUnreadState } from './useLeadUnreadState';

interface UseLeadDataProps {
  leadId: number | null;
  leadListUnreadMessage?: ModelLead['UnreadMessage'];
  onLeadUpdated?(lead: ModelLead): void;
  onResponsibleChangeSuccess?(): void | Promise<void>;
}

interface LeadStatusOverride {
  appliedAt: number;
  leadId: number;
  unreadMessage: ModelLead['UnreadMessage'];
  isNoAnswer: ModelLead['IsNoAnswer'];
}

export interface UseLeadDataResult {
  leadId: number | null;
  leadCardData: GetLeadCardResponse | undefined;
  leadDetails: LeadCardUserDetailsData | null;
  branches: LeadCardBranchItemData[];
  integrations: LeadCardIntegrationItemData[];
  rawBranches: NSmallGlobalEntities.ModelItem[];
  selectedBranchId: number | null;
  selectBranch(branchId: LeadCardBranchItemData['id']): void;
  isAddBranchModalOpen: boolean;
  openAddBranchModal(): void;
  closeAddBranchModal(): void;
  refetchLeadCard(): void;
  isLeadCardLoading: boolean;
  isLeadCardFetching: boolean;
  isLeadCardUninitialized: boolean;
  isLeadCardError: boolean;
  applyLeadUpdate(lead: ModelLead): void;
  applyManualUnreadLeadUpdate(lead: ModelLead): void;
  organizationComment: string;
  setOrganizationComment: React.Dispatch<React.SetStateAction<string>>;
}

const toBranchItem = (
  branch: NSmallGlobalEntities.ModelItem
): LeadCardBranchItemData => ({
  id: branch.Id,
  name: branch.Name ?? '',
});

// Дружелюбное название канала по SocialType — фолбэк для интеграций без
// собственного имени (например системная «Техническая поддержка», US 11021).
const getIntegrationTypeLabel = (socialType: string): string =>
  INTEGRATIONS_DICTIONARY.find(({ id }) => id === socialType)?.name ?? '';

const toIntegrationItems = (
  socialIntegrations?: GetLeadCardResponse['SocialIntegrations']
): LeadCardIntegrationItemData[] =>
  (socialIntegrations ?? [])
    .filter(({ Id, SocialType }) => Id && SocialType)
    .map(
      ({
        Id,
        SocialType,
        IntegrationName,
        AccountName,
        AccountLogin,
        Branch,
        RecommendChat,
      }) => ({
        id: Id,
        type: SocialType ?? '',
        name:
          IntegrationName ||
          AccountName ||
          AccountLogin ||
          getIntegrationTypeLabel(SocialType ?? ''),
        branchName: Branch ?? '',
        recommendChat: RecommendChat,
      })
    );

const SOCIAL_URL_PREFIXES: Partial<Record<string, string>> = {
  [INSTAGRAM]: 'https://www.instagram.com/',
  [VK]: 'https://vk.com/',
  [TELEGRAM]: 'https://t.me/',
  [TELEGRAM_PERSONAL]: 'https://t.me/',
  [MAX_MESSENGER]: 'https://max.ru/',
};

const EXTERNAL_LINK_REGEXP = /^https?:\/\//i;

// Контакт Telegram (личный) бывает задан номером телефона: переписку начинают
// по номеру, и CRM хранит его как имя пользователя. Username в Telegram не
// может начинаться с цифры, поэтому значение из одних цифр — это номер.
// Профиль по номеру открывается ссылкой t.me/+<номер>; t.me/<цифры> Telegram
// ищет как username и показывает, что пользователя нет (issue/11379).
const PHONE_NUMBER_REGEXP = /^\+?\d+$/;
const PHONE_SEPARATORS_REGEXP = /[\s()-]/g;

const getTelegramPhonePath = (value: string): string | null => {
  const phone = value.replace(PHONE_SEPARATORS_REGEXP, '');
  return PHONE_NUMBER_REGEXP.test(phone)
    ? `+${phone.replace(/^\+/, '')}`
    : null;
};

const formatName = (
  nameFirst?: string | null,
  nameLast?: string | null
): string => {
  const fullName = `${nameLast ?? ''} ${nameFirst ?? ''}`.trim();
  return fullName || 'Без имени';
};

const formatPhone = (phone?: string | null): string | null => {
  const trimmed = phone?.trim();
  if (!trimmed) return null;

  return trimmed.includes('*') || trimmed.startsWith('+')
    ? trimmed
    : `+${trimmed}`;
};

const toAvatarSrc = (photo?: string | null): string | null =>
  photo ? `data:image/png;base64,${photo}` : null;

const normalizeContactValue = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed || null;
};

const getSocialLink = (
  socialType: string,
  accountName: string
): string | undefined => {
  const value = normalizeContactValue(accountName);
  if (!value) return undefined;

  if (EXTERNAL_LINK_REGEXP.test(value)) return value;

  const prefix = SOCIAL_URL_PREFIXES[socialType];
  if (!prefix) return undefined;

  const accountPath = value.replace(/^@+/, '');
  if (!accountPath) return undefined;

  const telegramPhonePath =
    socialType === TELEGRAM_PERSONAL ? getTelegramPhonePath(accountPath) : null;

  return `${prefix}${telegramPhonePath ?? accountPath}`;
};

const toContactMethods = (
  clientView: GetLeadCardResponse['ClientView'],
  clientUsernames?: GetLeadCardResponse['ClientUsernames']
): LeadCardContactMethodData[] => {
  const contactMethods: LeadCardContactMethodData[] = [];
  const contactKeys = new Set<string>();
  const addContactMethod = (item: LeadCardContactMethodData) => {
    const value = normalizeContactValue(item.value);
    if (!value) return;

    const key = `${item.kind}-${item.socialType ?? ''}-${value.toLowerCase()}`;
    if (contactKeys.has(key)) return;

    contactKeys.add(key);
    contactMethods.push({ ...item, value });
  };

  const phone1 = formatPhone(clientView.Phone1);
  const phone2 = formatPhone(clientView.Phone2);
  const email = clientView.Email?.trim();

  if (phone1) {
    addContactMethod({
      id: 'phone-1',
      kind: 'phone',
      value: phone1,
      isVerified: clientView.IsPhoneVerified,
    });
  }

  if (phone2) {
    addContactMethod({
      id: 'phone-2',
      kind: 'phone',
      value: phone2,
    });
  }

  if (email) {
    addContactMethod({
      id: 'email',
      kind: 'email',
      socialType: EMAIL_HANDLER,
      value: email,
      href: `mailto:${email}`,
    });
  }

  (clientUsernames ?? []).forEach(({ IdS, Name }) => {
    const socialType = IdS;
    if (!socialType || socialType === EMAIL_HANDLER) return;

    const accountName = normalizeContactValue(Name);
    if (!accountName) return;

    addContactMethod({
      id: `social-username-${socialType}`,
      kind: 'social',
      socialType,
      value: accountName,
      href: getSocialLink(socialType, accountName),
    });
  });

  (clientView.Channels ?? []).forEach((channel) => {
    const socialType = channel.IdS;
    if (!socialType || socialType === EMAIL_HANDLER) return;

    const accountName = normalizeContactValue(channel.AccountName);
    if (!accountName) return;

    addContactMethod({
      id: `social-channel-${channel.Id}-${socialType}`,
      kind: 'social',
      socialType,
      value: accountName,
      href: getSocialLink(socialType, accountName),
    });
  });

  return contactMethods;
};

// Отметки пунктов проставляет useUserDetailsState по настройкам с бэка
// (us/11168); здесь только список вариантов.
const toCheckboxItems = (
  items: Omit<LeadCardCheckboxItemData, 'checked'>[]
): LeadCardCheckboxItemData[] =>
  items.map((item) => ({
    ...item,
    checked: true,
  }));

const toSourceItems = (
  channels: NClients.ClientForView['Channels']
): LeadCardCheckboxItemData[] => {
  const uniqueChannels = Array.from(
    new Map((channels ?? []).map((item) => [item.IdS, item])).values()
  );
  const items = uniqueChannels
    .map(({ Name, IdS }) => ({
      id: normalizeId(IdS),
      label: Name ?? '',
    }))
    .filter((item): item is Omit<LeadCardCheckboxItemData, 'checked'> =>
      Boolean(item.id)
    );

  return toCheckboxItems(items);
};

const toActionItems = (
  historyActionIds: NClients.ClientForView['HistoryActionIds']
): LeadCardCheckboxItemData[] => {
  const baseItems = (historyActionIds ?? [])
    .map(({ Key, Value }) => ({
      id: normalizeId(Key),
      label: Value ?? '',
    }))
    .filter((item): item is Omit<LeadCardCheckboxItemData, 'checked'> =>
      Boolean(item.id)
    );

  const customItems = LEAD_CARD_CUSTOM_ACTIONS.map(({ value, label }) => ({
    id: normalizeId(value),
    label: typeof label === 'string' ? label : String(value ?? ''),
  })).filter((item): item is Omit<LeadCardCheckboxItemData, 'checked'> =>
    Boolean(item.id)
  );

  const uniqueItems = Array.from(
    new Map(
      [...baseItems, ...customItems].map((item) => [item.id, item])
    ).values()
  );

  return toCheckboxItems(uniqueItems);
};

const toResponsibleEmployees = (
  users: GetLeadCardResponse['Users'],
  currentUserId: GetLeadCardResponse['UserId'],
  currentUserName?: string | null
): LeadCardResponsibleEmployeeData[] => {
  const employees = (users ?? []).map(({ Id, Name }) => ({
    id: Id,
    name: Name ?? '',
  }));

  const hasCurrentUser =
    currentUserId === null ||
    employees.some(({ id }) => Number(id) === currentUserId);

  if (hasCurrentUser || !currentUserName) return employees;

  return [
    ...employees,
    {
      id: currentUserId,
      name: currentUserName,
    },
  ];
};

export const useLeadData = ({
  leadId,
  leadListUnreadMessage,
  onLeadUpdated,
  onResponsibleChangeSuccess,
}: UseLeadDataProps): UseLeadDataResult => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { hasUserRightToShowContacts } = useClientsAccessRights();
  const dispatch = useAppDispatch();
  const pendingLeadCardRefreshId = useAppSelector(
    selectPendingLeadCardRefreshId
  );

  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  // forBranchId уходит на бэк только когда филиал выбрал сам пользователь.
  // Раньше в запрос попадал и филиал, пришедший в ответе GetLeadCard, то есть
  // мы возвращали бэку его же значение вторым запросом. Нового это не давало,
  // а если клиента в том филиале нет — бэк отвечал ошибкой «Клиент для лида
  // не найден в филиале», карточка оставалась без данных и история не
  // грузилась вовсе (ошибки в прод-логах бэка, август 2026).
  const [userSelectedBranchId, setUserSelectedBranchId] = useState<
    number | null
  >(null);
  const [isAddBranchModalOpen, setIsAddBranchModalOpen] = useState(false);
  const [organizationComment, setOrganizationComment] = useState<string>('');
  const [leadStatusOverride, setLeadStatusOverride] =
    useState<LeadStatusOverride | null>(null);

  const initialisedForLeadIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (initialisedForLeadIdRef.current === leadId) return;

    initialisedForLeadIdRef.current = null;
    setSelectedBranchId(null);
    setUserSelectedBranchId(null);
    setLeadStatusOverride(null);
  }, [leadId]);

  const queryArgs = useMemo(
    () =>
      leadId && ActiveBranchId
        ? {
            leadId,
            ActiveBranchId,
            ...(userSelectedBranchId !== null
              ? { forBranchId: userSelectedBranchId }
              : {}),
          }
        : null,
    [leadId, ActiveBranchId, userSelectedBranchId]
  );

  const {
    data: queriedLeadCardData,
    isLoading: isLeadCardLoading,
    isFetching: isLeadCardFetching,
    isUninitialized: isLeadCardUninitialized,
    isError: isLeadCardError,
    error: leadCardError,
    fulfilledTimeStamp,
    refetch,
    startedTimeStamp,
  } = useGetLeadCardQuery(queryArgs ?? skipToken);
  const queriedLeadCardDataRef = useRef(queriedLeadCardData);
  queriedLeadCardDataRef.current = queriedLeadCardData;

  // Keep mutation responses visible without refetching the card. A query that
  // starts after the mutation and then fulfils becomes the new source of truth.
  const leadCardData = useMemo<GetLeadCardResponse | undefined>(() => {
    if (!queriedLeadCardData) return undefined;
    if (!leadStatusOverride) return queriedLeadCardData;
    if (leadStatusOverride.leadId !== queriedLeadCardData.LeadId) {
      return queriedLeadCardData;
    }

    const hasNewerFulfilledQuery =
      typeof startedTimeStamp === 'number' &&
      typeof fulfilledTimeStamp === 'number' &&
      startedTimeStamp > leadStatusOverride.appliedAt &&
      fulfilledTimeStamp >= startedTimeStamp;

    if (hasNewerFulfilledQuery) return queriedLeadCardData;

    return {
      ...queriedLeadCardData,
      Lead: {
        ...queriedLeadCardData.Lead,
        UnreadMessage: leadStatusOverride.unreadMessage,
        IsNoAnswer: leadStatusOverride.isNoAnswer,
      },
    };
  }, [
    fulfilledTimeStamp,
    leadStatusOverride,
    queriedLeadCardData,
    startedTimeStamp,
  ]);

  useErrorHandler([{ flag: isLeadCardError, error: leadCardError }]);

  const responsibleChangeSuccessHandler = useCallback(async () => {
    refetch();
    await onResponsibleChangeSuccess?.();
  }, [onResponsibleChangeSuccess, refetch]);

  const {
    changeResponsible,
    hasRightToChangeResponsible,
    isResponsibleChanging,
  } = useLeadResponsibleChange({
    leadId,
    onSuccess: responsibleChangeSuccessHandler,
  });

  const isLeadCardDataActual =
    leadId !== null && leadCardData?.LeadId === leadId;

  const applyLeadUpdate = useCallback(
    (updatedLead: ModelLead) => {
      const currentLeadCardData = queriedLeadCardDataRef.current;

      if (currentLeadCardData?.LeadId === updatedLead.Id) {
        setLeadStatusOverride({
          appliedAt: Date.now(),
          leadId: updatedLead.Id,
          unreadMessage: updatedLead.UnreadMessage,
          isNoAnswer: updatedLead.IsNoAnswer,
        });
      }

      onLeadUpdated?.(updatedLead);
    },
    [onLeadUpdated]
  );

  const { applyManualUnreadLeadUpdate } = useLeadUnreadState({
    activeBranchId: ActiveBranchId,
    isLeadCardDataActual,
    leadId,
    unreadMessage:
      leadListUnreadMessage !== undefined
        ? leadListUnreadMessage
        : leadCardData?.Lead.UnreadMessage,
    onLeadUpdated: applyLeadUpdate,
  });

  useEffect(() => {
    if (!leadCardData || leadId === null) return;
    if (leadCardData.LeadId !== leadId) return;
    if (initialisedForLeadIdRef.current === leadId) return;

    initialisedForLeadIdRef.current = leadId;
    setSelectedBranchId(leadCardData.BranchId ?? null);
    setOrganizationComment(leadCardData.ClientView.CompanyName || '');
  }, [leadCardData, leadId]);

  const rawBranches = useMemo<NSmallGlobalEntities.ModelItem[]>(
    () => (isLeadCardDataActual ? leadCardData?.Branches ?? [] : []),
    [isLeadCardDataActual, leadCardData?.Branches]
  );

  const branches = useMemo<LeadCardBranchItemData[]>(
    () => rawBranches.map(toBranchItem),
    [rawBranches]
  );

  const integrations = useMemo<LeadCardIntegrationItemData[]>(
    () =>
      isLeadCardDataActual
        ? toIntegrationItems(leadCardData?.SocialIntegrations)
        : [],
    [isLeadCardDataActual, leadCardData?.SocialIntegrations]
  );

  const leadDetails = useMemo<LeadCardUserDetailsData | null>(() => {
    if (!leadCardData || !isLeadCardDataActual) return null;

    const { ClientView, ClientUsernames, Lead, UserId, Users } = leadCardData;
    const responsibleEmployees = toResponsibleEmployees(
      Users,
      UserId,
      Lead?.UserName
    );

    return {
      profile: {
        id: ClientView.Id,
        name: formatName(ClientView.NameFirst, ClientView.NameLast),
        avatarBase64: toAvatarSrc(ClientView.Photo),
        balance: ClientView.Balance,
      },
      stats: {
        visitsCount: ClientView.CountVisits,
        salesCount: ClientView.MoneySpent,
        firstVisitDate: ClientView.DateVisitFirst,
        lastVisitDate: ClientView.DateVisitLast,
      },
      clientNote: ClientView.Comment,
      // Без права «Просмотр контактных данных клиентов» блока контактов нет:
      // телефоны, e-mail и аккаунты не попадают в разметку (issue/11538).
      contactMethods: hasUserRightToShowContacts
        ? toContactMethods(ClientView, ClientUsernames)
        : [],
      responsibleEmployees,
      defaultResponsibleEmployeeId: UserId,
      isResponsibleChangeDisabled: !hasRightToChangeResponsible,
      isResponsibleChanging,
      onResponsibleChange: changeResponsible,
      shouldPersistFilters: true,
      sourceItems: toSourceItems(ClientView.Channels),
      actionItems: toActionItems(ClientView.HistoryActionIds),
      tags: toTagItems(ClientView.ClientTags),
    };
  }, [
    changeResponsible,
    hasRightToChangeResponsible,
    hasUserRightToShowContacts,
    isResponsibleChanging,
    isLeadCardDataActual,
    leadCardData,
  ]);

  const selectBranch = useCallback((branchId: LeadCardBranchItemData['id']) => {
    const numericId =
      typeof branchId === 'number' ? branchId : Number(branchId);

    if (!Number.isFinite(numericId)) return;

    setSelectedBranchId(numericId);
    setUserSelectedBranchId(numericId);
  }, []);

  const openAddBranchModal = () => setIsAddBranchModalOpen(true);

  const closeAddBranchModal = () => setIsAddBranchModalOpen(false);

  const refetchLeadCard = useCallback(() => {
    if (queryArgs === null) return;

    refetch();
  }, [queryArgs, refetch]);

  // Consume a pending refresh request for this lead (e.g. after saving the
  // client note on the edit client page). Refetch once for the matching lead
  // and clear the request so returning to the card shows fresh data without a
  // manual page refresh, and other leads never trigger an extra refetch.
  useEffect(() => {
    if (pendingLeadCardRefreshId === null) return;
    if (pendingLeadCardRefreshId !== leadId) return;

    refetchLeadCard();
    dispatch(clearLeadCardRefresh());
  }, [dispatch, leadId, pendingLeadCardRefreshId, refetchLeadCard]);

  return {
    leadId,
    leadCardData,
    leadDetails,
    branches,
    integrations,
    rawBranches,
    selectedBranchId,
    selectBranch,
    isAddBranchModalOpen,
    openAddBranchModal,
    closeAddBranchModal,
    refetchLeadCard,
    isLeadCardLoading,
    isLeadCardFetching,
    isLeadCardUninitialized,
    isLeadCardError,
    applyLeadUpdate,
    applyManualUnreadLeadUpdate,
    organizationComment,
    setOrganizationComment,
  };
};
